import Event from '@/assets/js/event'
import { useAuthStore } from '@/stores/auth'

let analyticsContext = null

export function setAnalyticsContext(context) {
    analyticsContext = context
}

function readCookie(name) {
    if (typeof document === 'undefined' || !name) {
        return null
    }

    const escapedName = String(name).replace(/[.*+?^${}()|[\]\\]/g, '\\$&')
    const match = document.cookie.match(new RegExp(`(?:^|; )${escapedName}=([^;]*)`))
    return match ? decodeURIComponent(match[1]) : null
}

function toNumber(value) {
    const normalized = Number(value)
    return Number.isFinite(normalized) ? normalized : 0
}

function buildUserData(overrides = {}) {
    const authStore = useAuthStore()
    const userId = authStore.user?.id

    return {
        external_id: userId != null ? String(userId) : null,
        fbc: readCookie('_fbc'),
        fbp: readCookie('_fbp'),
        client_user_agent: typeof navigator !== 'undefined' ? navigator.userAgent : null,
        ...overrides,
    }
}

function buildItemPayload(item) {
    if (!item) {
        return null
    }

    return {
        id: item.id != null ? String(item.id) : null,
        title: item.title ?? item.name ?? null,
        quantity: toNumber(item.quantity || 1),
        item_price: toNumber(item.price),
    }
}

function buildItemsPayload(items = []) {
    return items.map(buildItemPayload).filter(Boolean)
}

function getEventSourceUrl(sourceUrl) {
    if (sourceUrl) {
        return sourceUrl
    }

    return typeof window !== 'undefined' ? window.location.href : null
}

export function sendFacebookEvent(title, payload = {}, context = null) {
    // Берём явно переданный context, а иначе используем globalProperties,
    // зарегистрированные один раз при старте приложения.
    const event = new Event(context || analyticsContext)

    event.send(title, {
        ...payload,
        user_data: buildUserData(payload.user_data),
        event_source_url: getEventSourceUrl(payload.event_source_url),
    })
}

export function buildAddToCartEvent(product, quantity = 1, source = null) {
    const normalizedQuantity = toNumber(quantity || 1)
    const unitPrice = toNumber(product?.price)

    return {
        event_name: 'AddToCart',
        event_id: `add_to_cart:${product?.id ?? 'unknown'}:${Date.now()}`,
        custom_data: {
            content_name: product?.title ?? null,
            content_ids: product?.id != null ? [String(product.id)] : [],
            content_type: 'product',
            contents: buildItemsPayload([
                {
                    id: product?.id,
                    title: product?.title,
                    quantity: normalizedQuantity,
                    price: unitPrice,
                },
            ]),
            currency: 'VND',
            value: unitPrice * normalizedQuantity,
            source,
            restaurant_id: product?.restaurantId != null ? String(product.restaurantId) : null,
        },
    }
}

export function buildViewCartEvent(cart) {
    return {
        event_name: 'ViewCart',
        event_id: `view_cart:${Date.now()}`,
        custom_data: {
            content_type: 'product',
            contents: buildItemsPayload(cart?.items ?? []),
            content_ids: (cart?.items ?? []).map((item) => String(item.id)),
            currency: 'VND',
            value: toNumber(cart?.totalAmount),
            num_items: toNumber(cart?.totalCount),
            restaurant_id: cart?.restaurantId != null ? String(cart.restaurantId) : null,
            restaurant_title: cart?.restaurantTitle ?? null,
        },
    }
}

export function buildCheckoutIntentEvent(cart, paymentMethod, { stage, orderId = null } = {}) {
    return {
        event_name: stage === 'confirmed' ? 'InitiateCheckout' : 'CheckoutButtonClick',
        event_id: `${stage || 'checkout'}:${orderId ?? 'draft'}:${Date.now()}`,
        custom_data: {
            content_type: 'product',
            contents: buildItemsPayload(cart?.items ?? []),
            content_ids: (cart?.items ?? []).map((item) => String(item.id)),
            currency: 'VND',
            value: toNumber(cart?.totalAmount),
            num_items: toNumber(cart?.totalCount),
            payment_method: paymentMethod ?? null,
            stage,
            restaurant_id: cart?.restaurantId != null ? String(cart.restaurantId) : null,
        },
    }
}

function getPurchaseRestaurantMeta(order) {
    const restaurantId = order?.restaurantId ?? order?.restaurant_id ?? null
    return {
        restaurant_id: restaurantId != null ? String(restaurantId) : null,
        restaurant_title: order?.restaurantTitle ?? order?.restaurant_title ?? null,
    }
}

function getPurchaseDeliveryFee(paymentStore) {
    return toNumber(paymentStore?.rawOrder?.delivery_fee ?? paymentStore?.rawOrder?.deliveryFee)
}

export function buildPurchaseEvent(paymentStore) {
    const order = paymentStore?.rawOrder
    const items = order?.items ?? []

    return {
        event_name: 'Purchase',
        event_id: paymentStore?.orderId ? `purchase:${paymentStore.orderId}` : `purchase:${Date.now()}`,
        custom_data: {
            content_type: 'product',
            contents: buildItemsPayload(items),
            content_ids: items.map((item) => String(item.id)),
            currency: paymentStore?.payment?.currency ?? 'VND',
            value: toNumber(paymentStore?.totalAmount),
            num_items: items.reduce((total, item) => total + toNumber(item.quantity || 1), 0),
            order_id: paymentStore?.orderId ?? null,
            payment_provider: paymentStore?.payment?.provider ?? null,
            ...getPurchaseRestaurantMeta(order),
        },
    }
}

export function buildPurchaseCourierEvent(paymentStore) {
    const order = paymentStore?.rawOrder

    return {
        event_name: 'PurchaseCourier',
        event_id: paymentStore?.orderId
            ? `purchase_courier:${paymentStore.orderId}`
            : `purchase_courier:${Date.now()}`,
        custom_data: {
            content_type: 'product',
            currency: paymentStore?.payment?.currency ?? 'VND',
            value: getPurchaseDeliveryFee(paymentStore),
            order_id: paymentStore?.orderId ?? null,
            payment_provider: paymentStore?.payment?.provider ?? null,
            ...getPurchaseRestaurantMeta(order),
        },
    }
}

export function buildPaymentFailedEvent(paymentStore, reason = null) {
    return {
        event_name: 'PaymentFailed',
        event_id: paymentStore?.orderId ? `payment_failed:${paymentStore.orderId}` : `payment_failed:${Date.now()}`,
        custom_data: {
            order_id: paymentStore?.orderId ?? null,
            payment_provider: paymentStore?.payment?.provider ?? null,
            payment_method: paymentStore?.paymentMethod ?? null,
            value: toNumber(paymentStore?.totalAmount || paymentStore?.amountDue),
            currency: paymentStore?.payment?.currency ?? 'VND',
            reason,
        },
    }
}

export function buildAuthEvent(type, role) {
    return {
        event_name: type,
        event_id: `${type}:${role || 'unknown'}:${Date.now()}`,
        custom_data: {
            content_type: 'auth',
            source: role || null,
        },
    }
}

export function buildSearchEvent(query, resultCount = null) {
    const searchTerm = String(query || '').trim()

    return {
        event_name: 'Search',
        event_id: `search:${searchTerm}:${Date.now()}`,
        custom_data: {
            content_type: 'product',
            search_term: searchTerm,
            result_count: resultCount,
            source: 'client-search',
        },
    }
}

export function buildViewContentEvent(product) {
    return {
        event_name: 'ViewContent',
        event_id: `view_content:${product?.id ?? 'unknown'}:${Date.now()}`,
        custom_data: {
            content_name: product?.title ?? null,
            content_ids: product?.id != null ? [String(product.id)] : [],
            content_type: 'product',
            contents: buildItemsPayload([
                {
                    id: product?.id,
                    title: product?.title,
                    quantity: 1,
                    price: product?.price,
                },
            ]),
            currency: 'VND',
            value: toNumber(product?.price),
            restaurant_id: product?.restaurantId != null ? String(product.restaurantId) : null,
        },
    }
}

export function buildViewThanksPageEvent(orderId = null) {
    return {
        event_name: 'ViewThanksPage',
        event_id: orderId ? `view_thanks_page:${orderId}` : `view_thanks_page:${Date.now()}`,
        custom_data: {
            order_id: orderId || null,
        },
    }
}